import { HOLIDAY_RULES } from '../../data/holidays-de';
import { NUTRITION } from '../../data/nutrition';
import { PRODUCTS } from '../../data/products';
import { RECIPES } from '../../data/recipes';
import { STAPLE_PRODUCT_IDS } from '../../data/staples';
import { STORES } from '../../data/stores';
import { addDays, daysInMonth, type IsoDate, type Period } from '../../domain/dates';
import { applyNoEnergySwap } from '../../domain/energy-swap';
import { firstOpenDay } from '../../domain/holidays';
import type { Plan } from '../../domain/plan';
import { withClosedShopWarnings } from '../../domain/planner/closed-shops';
import { continueShopping, replanFrom, type DoneState } from '../../domain/planner/continue';
import { dinnerAlternatives } from '../../domain/planner/alternatives';
import { replaceDinner, setDinnerLocked } from '../../domain/planner/edit';
import { generatePlan } from '../../domain/planner/generate';
import { backupKit, recipeOf, type PlanRequest } from '../../domain/planner/request';
import { createCatalog } from '../../domain/price-book';
import { isQuick } from '../../domain/recipe';
import type { ShoppingTrip } from '../../domain/shopping';
import type { AppData, StoredPlan, StoredTrip } from './app-data';

/**
 * DE: Änderungen am laufenden Plan, wie sie die Bildschirme auslösen.
 * EN: Changes to the running plan as the screens trigger them.
 */
export type PlanEdit =
  | { kind: 'replace'; date: IsoDate; recipeId: string }
  | { kind: 'lock'; date: IsoDate; locked: boolean }
  | { kind: 'no-energy'; recipeId: string }
  | { kind: 'low-energy'; date: IsoDate }
  | { kind: 'plan-again'; seed: number }
  | { kind: 'recalculate-trips'; planStart: IsoDate };

export interface EditResult {
  stored: StoredPlan;
  notice?: { movedTo: IsoDate | null; useSoon: string[] };
}

const planEnd = (stored: StoredPlan) => addDays(stored.period.start, stored.period.days);

/**
 * DE: Planungsanfrage aus den gespeicherten Daten und den eingebauten Katalogen.
 * EN: Planning request from the stored data and the built-in catalogues.
 */
export function buildRequest(
  data: AppData,
  stored: Pick<StoredPlan, 'period' | 'seed'>,
  earliestTrip?: IsoDate,
): PlanRequest {
  return {
    household: data.household,
    period: stored.period,
    recipes: RECIPES,
    book: {
      catalog: createCatalog(PRODUCTS),
      stores: STORES,
      offers: data.offers,
      ownPrices: data.ownPrices,
    },
    nutrition: NUTRITION,
    pantry: data.pantry,
    locked: [],
    seed: stored.seed,
    staples: STAPLE_PRODUCT_IDS,
    holidays: HOLIDAY_RULES,
    earliestTrip,
  };
}

/**
 * DE: Plan, der heute läuft.
 * EN: Plan that runs today.
 */
export function currentPlan(data: AppData, today: IsoDate): StoredPlan | undefined {
  return data.plans.find((stored) => stored.period.start <= today && today < planEnd(stored));
}

/**
 * DE: Nächster Plan nach heute (im Voraus erstellt).
 * EN: Next plan after today (created ahead).
 */
export function nextPlan(data: AppData, today: IsoDate): StoredPlan | undefined {
  return data.plans.find((stored) => stored.period.start > today);
}

function periodFor(start: IsoDate, kind: 'month' | 'week'): Period {
  if (kind === 'week') return { start, days: 7 };
  const [year = 0, month = 0, day = 0] = start.split('-').map(Number);
  return { start, days: daysInMonth(year, month) - day + 1 };
}

/**
 * DE: Gespeicherte Einkäufe: behaltene zuerst, neue mit eindeutiger Kennung; Häkchen eines offenen
 * Einkaufs am selben Tag bleiben, soweit die Artikel noch dabei sind.
 * EN: Stored trips: kept ones first, new ones with a unique id; ticks of an open trip on the same day
 * stay as far as its items are still there.
 */
export function toStoredTrips(
  planStart: IsoDate,
  fresh: readonly ShoppingTrip[],
  kept: readonly StoredTrip[],
  previous: readonly StoredTrip[],
): StoredTrip[] {
  const result = [...kept];
  for (const trip of fresh) {
    const sameDay = result.filter((other) => other.date === trip.date).length;
    const keys = trip.items.map((item) => `${item.productId}|${item.storeId}`);
    const open = previous.find((other) => other.date === trip.date && !other.bought);
    const checked = (open?.checked ?? []).filter((key) => keys.includes(key));
    result.push({ ...trip, id: `${planStart}/${trip.date}#${sameDay}`, bought: false, checked });
  }
  return result;
}

/**
 * DE: Neuen Plan erstellen: ab dem ersten offenen Tag oder direkt nach dem laufenden Plan.
 * EN: Create a new plan: from the first open day or right after the running plan.
 */
export function createPlan(
  data: AppData,
  options: { kind: 'month' | 'week'; today: IsoDate; seed: number },
): StoredPlan {
  const { kind, today, seed } = options;
  const latest = data.plans.at(-1);
  const continues = latest !== undefined && planEnd(latest) > today;
  const calendar = { ...data.household, holidays: HOLIDAY_RULES };
  const start = continues ? planEnd(latest) : firstOpenDay(today, calendar);
  const period = periodFor(start, kind);
  const request = buildRequest(data, { period, seed }, continues ? today : undefined);
  const { plan, shopping } = generatePlan(request);
  const reserved = backupKit(request).map((recipe) => ({ date: start, recipeId: recipe.id }));
  const trips = toStoredTrips(start, shopping.trips, [], []);
  return { period, seed, createdOn: today, reserved, plan, trips };
}

function withShopping(
  stored: StoredPlan,
  plan: Plan,
  request: PlanRequest,
  done: DoneState,
): StoredPlan {
  const shopping = continueShopping(plan, request, done);
  const fresh = shopping.trips.slice(done.doneTrips.length);
  const kept = stored.trips.filter((trip) => done.doneTrips.includes(trip));
  return {
    ...stored,
    plan: withClosedShopWarnings(plan, shopping.late),
    trips: toStoredTrips(stored.period.start, fresh, kept, stored.trips),
  };
}

function dayOf(plan: Plan, date: IsoDate) {
  const day = plan.days.find((planned) => planned.date === date);
  if (!day) throw new Error(`No planned day on ${date}`);
  return day;
}

// DE: Ein Tag mit wenig Kraft bekommt das günstigste schnelle Gericht; die anderen Tage bleiben.
// EN: A low-energy day gets the cheapest quick dinner; the other days stay.
function quickDay(stored: StoredPlan, date: IsoDate, request: PlanRequest, done: DoneState) {
  const day = dayOf(stored.plan, date);
  if (
    day.dinner.locked ||
    isQuick(recipeOf(request, day.dinner.recipeId), request.household.quick)
  ) {
    return stored;
  }
  const [choice] = dinnerAlternatives(stored.plan, date, request);
  if (!choice) return stored;
  return withShopping(stored, replaceDinner(stored.plan, day, choice, request), request, done);
}

function planAgain(stored: StoredPlan, request: PlanRequest, done: DoneState): StoredPlan {
  const result = replanFrom(stored.plan, request, done);
  const fresh = result.shopping.trips.slice(done.doneTrips.length);
  const kept = stored.trips.filter((trip) => done.doneTrips.includes(trip));
  const trips = toStoredTrips(stored.period.start, fresh, kept, stored.trips);
  return { ...stored, seed: request.seed, plan: result.plan, trips };
}

// DE: Wird ein Notfallgericht heute gekocht, ist sein Vorrat verbraucht und muss nicht neu gekauft werden.
// EN: When a backup-kit dinner is cooked today, its kit is used up and need not be bought again.
function releaseKit(stored: StoredPlan, recipeId: string): StoredPlan {
  // DE: Ohne Treffer ist der Index -1, dann bleibt die Liste gleich.
  // EN: Without a match the index is -1, so the list stays the same.
  const index = stored.reserved.findIndex((reserve) => reserve.recipeId === recipeId);
  return { ...stored, reserved: stored.reserved.filter((_, other) => other !== index) };
}

function planToEdit(data: AppData, today: IsoDate, edit: PlanEdit): StoredPlan {
  // DE: Vergangene Tage bleiben, wie sie waren (Spezifikation 5.9).
  // EN: Past days stay as they were (spec 5.9).
  if ('date' in edit && edit.date < today) throw new Error(`${edit.date} is in the past`);
  const stored =
    edit.kind === 'recalculate-trips'
      ? data.plans.find((plan) => plan.period.start === edit.planStart)
      : currentPlan(data, today);
  if (!stored)
    throw new Error(`No plan for ${edit.kind === 'recalculate-trips' ? edit.planStart : today}`);
  return stored;
}

/**
 * DE: Laufenden Plan ändern; vergangene Tage und erledigte Einkäufe bleiben (Spezifikation 5.9).
 * EN: Change the running plan; past days and done trips stay (spec 5.9).
 */
export function editPlan(data: AppData, today: IsoDate, edit: PlanEdit): EditResult {
  const stored = planToEdit(data, today, edit);
  const request = buildRequest(data, stored);
  const doneTrips = stored.trips.filter((trip) => trip.bought || trip.date < today);
  // DE: Ein Plan, der erst später beginnt, rechnet ab seinem Start; Einkäufe dürfen ab heute liegen.
  // EN: A plan that starts later counts from its start; trips may fall from today on.
  const from = stored.period.start > today ? stored.period.start : today;
  const done: DoneState = {
    today: from,
    earliestTrip: today,
    doneTrips,
    reserved: stored.reserved,
  };
  switch (edit.kind) {
    case 'lock':
      return { stored: { ...stored, plan: setDinnerLocked(stored.plan, edit.date, edit.locked) } };
    case 'replace': {
      const plan = replaceDinner(
        stored.plan,
        dayOf(stored.plan, edit.date),
        recipeOf(request, edit.recipeId),
        request,
      );
      const kept = edit.date === today ? releaseKit(stored, edit.recipeId) : stored;
      return { stored: withShopping(kept, plan, request, { ...done, reserved: kept.reserved }) };
    }
    case 'no-energy': {
      const swap = applyNoEnergySwap(stored.plan, today, edit.recipeId, request);
      const notice = { movedTo: swap.movedTo, useSoon: swap.useSoon };
      const kept = releaseKit(stored, edit.recipeId);
      const shopped = withShopping(kept, swap.plan, request, { ...done, reserved: kept.reserved });
      return { stored: shopped, notice };
    }
    case 'low-energy': {
      const lowEnergyDates = [...new Set([...data.household.lowEnergyDates, edit.date])];
      const household = { ...data.household, lowEnergyDates };
      return {
        stored: quickDay(stored, edit.date, buildRequest({ ...data, household }, stored), done),
      };
    }
    case 'recalculate-trips':
      // DE: Nach geänderten Feiertagen oder Ruhetagen: gleiche Gerichte, Einkäufe ab heute neu.
      // EN: After changed holidays or closed days: same dinners, trips from today recalculated.
      return { stored: withShopping(stored, stored.plan, request, done) };
    case 'plan-again':
      return {
        stored: planAgain(stored, buildRequest(data, { ...stored, seed: edit.seed }), done),
      };
  }
}
